// ** React Imports
import { useState, useEffect } from 'react'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid';
import FormControl from '@mui/material/FormControl'
import TextField from '@mui/material/TextField'
import {
    Autocomplete,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Fab,
    FormHelperText,
    IconButton,
    Tab,
    Tooltip,
    Typography,
} from '@mui/material'
import { useNavigate } from "react-router-dom";
import axios from 'axios'
import Icon from '../icon';
import apiUrl from '../../configs/constant';
import MsiDatagrid from '../custom/MsiDatagridAuto';

import moment from 'moment'
import { useToast } from '../ToastProvider'
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Controller, useForm } from 'react-hook-form';
import Swal from 'sweetalert2';

interface Props {
    mmgudangId?: number | string | null;
}

export default function Index({ mmgudangId }: Props) {
    const userData = JSON.parse(localStorage.getItem('user')!)
    const token = userData.token
    const { showToast, hideToast } = useToast();

    // ** State
    const [tglAwal, setTglAwal] = useState<string>(moment().subtract(3, 'months').startOf('month').format('YYYY-MM-DD'))
    const [tglAkhir, setTglAkhir] = useState<string>(moment().endOf('month').format('YYYY-MM-DD'))
    const [tabs, setTabs] = useState<string>('0')

    const handleChangeTabs = (event: React.SyntheticEvent, newValue: string) => {
        setTabs(newValue);
    };

    const [stockRack, setStockRack] = useState<any[]>([])
    const [listRekap, setListRekap] = useState<any[]>([])
    const [listBarang, setListBarang] = useState<any[]>([])
    const [barang, setBarang] = useState<any>(null)

    const [kartuStock, setKartuStock] = useState<any[]>([])
    const [rekapLoading, setRekapLoading] = useState<boolean>(false)
    const [kartuLoading, setKartuLoading] = useState<boolean>(false)

    useEffect(() => {
        getRekap()
    }, [mmgudangId])

    const navigate = useNavigate();

    const getStock = () => {
        let url = apiUrl.apiUrl + '/gudang/stock-gudang/stock-rack'
        axios
            .get(url, {
                params: {
                    tgl_akhir: tglAkhir,
                    mmgudang_id: mmgudangId
                },
                headers: { Authorization: `Bearer ${token}` }
            })
            .then(response => {
                if (response.data.success) {
                    const data = response.data.data
                    data.forEach((item: any) => {
                        item.id = 0
                    })
                    setStockRack(data)
                } else {

                }
            })
    }

    const getRekap = () => {
        setRekapLoading(true)
        let url = apiUrl.apiUrl + '/gudang/stock-gudang/rekap-stock'
        axios
            .get(url, {
                params: {
                    mmgudang_id: mmgudangId,
                    tgl_awal: tglAwal,
                    tgl_akhir: tglAkhir
                },
                headers: { Authorization: `Bearer ${token}` }
            })
            .then(response => {
                if (response.data.success) {
                    const data = response.data.data
                    if (Array.isArray(data)) {
                        data.forEach((item: any) => {
                            item.id = item.barang
                        })
                    }
                    setListRekap(data)
                } else {
                    showToast(response.data.message, 'error')
                }
            })
            .catch(error => showToast(error.response?.data?.message || error.message, 'error'))
            .finally(() => setRekapLoading(false))
    }

    const getBarang = () => {
        const url = apiUrl.apiUrl + '/gudang/stock-opname/get-barang/' + mmgudangId

        axios
            .get(url, {
                headers: { Authorization: `Bearer ${token}` }
            })
            .then(response => {
                if (response.data.success) {
                    const data = Array.isArray(response.data.data) ? response.data.data : []

                    const preparedData = data.map((item: any) => ({
                        ...item,

                        // Disiapkan sekali saat data diterima supaya pencarian tidak
                        // perlu normalize seluruh data pada setiap tombol keyboard.
                        _searchText: normalizeSearchText(
                            `${item?.kode ?? ''} ${item?.nama ?? ''} ${item?.jenis_barang ?? ''}`
                        ),
                        _searchKode: normalizeSearchText(item?.kode),
                        _searchNama: normalizeSearchText(item?.nama)
                    }))

                    setListBarang(preparedData)
                } else {
                    showToast(response.data.message, 'error')
                }
            })
            .catch(error => {
                showToast(error?.response?.data?.message || error.message || 'Gagal mengambil data barang.', 'error')
            })
    }

    const getKartuStock = () => {
        setKartuLoading(true)
        let url = apiUrl.apiUrl + '/gudang/stock-gudang/kartu-stock'
        axios
            .get(url, {
                params: {
                    mmgudang_id: mmgudangId,
                    mmbarang_id: barang?.id,
                    tgl_awal: tglAwal,
                    tgl_akhir: tglAkhir
                },
                headers: { Authorization: `Bearer ${token}` }
            })
            .then(response => {
                if (response.data.success) {
                    const data = response.data.data
                    if (Array.isArray(data)) {
                        data.forEach((item: any) => {
                            item.id = item.mutasi_id
                        })
                    }
                    setKartuStock(data)

                } else {
                    showToast(response.data.message, 'error')
                }
            })
            .catch(error => showToast(error.response?.data?.message || error.message, 'error'))
            .finally(() => setKartuLoading(false))
    }

    const normalizeSearchText = (value: unknown) =>
        String(value ?? '')
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/[^a-z0-9]+/g, ' ')
            .trim()
            .replace(/\s+/g, ' ')

    const filterOptions = (options: any[], { inputValue }: { inputValue: string }) => {
        const query = normalizeSearchText(inputValue)

        // Jangan render seluruh master barang saat input masih kosong.
        if (!query) return options.slice(0, 50)

        const keywords = query.split(' ').filter(Boolean)

        const exactMatches: any[] = []
        const startsWithMatches: any[] = []
        const containsMatches: any[] = []

        for (const option of options) {
            const searchText = option?._searchText ?? ''
            const searchKode = option?._searchKode ?? ''
            const searchNama = option?._searchNama ?? ''

            // Semua kata harus ada agar hasil tetap akurat.
            const isMatch = keywords.every(keyword => searchText.includes(keyword))

            if (!isMatch) continue

            // Ranking ringan tanpa sort seluruh array.
            if (searchKode === query || searchNama === query) {
                exactMatches.push(option)
            } else if (searchKode.startsWith(query) || searchNama.startsWith(query)) {
                startsWithMatches.push(option)
            } else {
                containsMatches.push(option)
            }

            // Cukup banyak untuk dropdown, tidak perlu proses ribuan hasil.
            if (
                exactMatches.length +
                startsWithMatches.length +
                containsMatches.length >=
                100
            ) {
                break
            }
        }

        return [
            ...exactMatches,
            ...startsWithMatches,
            ...containsMatches
        ].slice(0, 100)
    }


    return (
        <Grid container spacing={3} sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center' }}>
            <Grid item xs={12}>
                <TabContext value={tabs}>
                    <TabList
                        scrollButtons
                        variant='fullWidth'
                        onChange={handleChangeTabs}
                        aria-label='forced scroll tabs example'
                    >
                        <Tab value='0' label='Rekap Stock' onClick={getRekap} icon={<Icon icon='mdi:warehouse' />} />
                        <Tab value='2' label='Kartu Stock' onClick={getBarang} icon={<Icon icon='mdi:warehouse' />} />
                    </TabList>
                    <TabPanel value='0'>
                        <Box>
                            <Grid
                                container
                                sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-start' }}
                                spacing={5}
                            >
                                <Grid item xs={12} md={3}>
                                    <FormControl fullWidth>
                                        <TextField
                                            label='Tanggal Awal'
                                            type='date'
                                            onChange={(e) => setTglAwal(e.target.value)}
                                            value={tglAwal}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <FormControl fullWidth>

                                        <TextField
                                            label='Tanggal Akhir'
                                            type='date'
                                            onChange={(e) => setTglAkhir(e.target.value)}
                                            value={tglAkhir}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <FormControl fullWidth>
                                        <Button
                                            variant='contained'
                                            sx={{ mt: 1 }}
                                            startIcon={<Icon icon='mdi:magnify' />}
                                            onClick={() => getRekap()}
                                        >
                                            Cari Data
                                        </Button>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </Box>
                        <MsiDatagrid title="Ringkasan stok" loading={rekapLoading} array={listRekap} hiddenColumns={['id']} />
                    </TabPanel>
                    <TabPanel value='2'>
                        <Box>
                            <Grid
                                container
                                sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-start' }}
                                spacing={5}
                            >
                                <Grid item xs={12} md={3}>
                                    <FormControl fullWidth>
                                        <TextField
                                            label='Tanggal Awal'
                                            type='date'
                                            onChange={(e) => setTglAwal(e.target.value)}
                                            value={tglAwal}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <FormControl fullWidth>

                                        <TextField
                                            label='Tanggal Akhir'
                                            type='date'
                                            onChange={(e) => setTglAkhir(e.target.value)}
                                            value={tglAkhir}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Autocomplete
                                        options={listBarang}
                                        value={barang}
                                        onChange={(_, value) => setBarang(value)}
                                        getOptionLabel={option => {
                                            const kode = option?.kode ? `[${option.kode}] ` : ''

                                            return `${kode}${option?.nama ?? ''}`
                                        }}
                                        isOptionEqualToValue={(option, value) =>
                                            String(option?.id) === String(value?.id)
                                        }
                                        filterOptions={filterOptions}
                                        noOptionsText='Barang tidak ditemukan'
                                        autoHighlight
                                        selectOnFocus
                                        clearOnBlur
                                        handleHomeEndKeys
                                        openOnFocus={false}
                                        limitTags={1}
                                        renderOption={(props, option) => (
                                            <li {...props} key={option.id}>
                                                <Box sx={{ width: '100%', py: 0.5 }}>
                                                    <Typography
                                                        variant='body2'
                                                        sx={{ fontWeight: 600, lineHeight: 1.4 }}
                                                    >
                                                        {option.nama || '-'}
                                                    </Typography>

                                                    <Box
                                                        sx={{
                                                            mt: 0.5,
                                                            display: 'flex',
                                                            flexWrap: 'wrap',
                                                            gap: 1,
                                                            alignItems: 'center'
                                                        }}
                                                    >
                                                        {option.kode && (
                                                            <Typography
                                                                variant='caption'
                                                                color='primary'
                                                                sx={{ fontWeight: 600 }}
                                                            >
                                                                {option.kode}
                                                            </Typography>
                                                        )}

                                                        {option.jenis_barang && (
                                                            <Typography variant='caption' color='text.secondary'>
                                                                • {option.jenis_barang}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Box>
                                            </li>
                                        )}
                                        renderInput={params => (
                                            <TextField
                                                {...params}
                                                label='Barang'
                                                placeholder='Cari kode, nama, atau jenis barang...'
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <FormControl fullWidth>
                                        <Button
                                            variant='contained'
                                            sx={{ mt: 1 }}
                                            startIcon={<Icon icon='mdi:magnify' />}
                                            disabled={barang == null ? true : false}
                                            onClick={() => getKartuStock()}
                                        >
                                            Cari Data
                                        </Button>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </Box>
                        <MsiDatagrid title="Riwayat kartu stok" loading={kartuLoading} array={kartuStock} hiddenColumns={['id', 'doc_type', 'mutasi_id']} />
                    </TabPanel>
                </TabContext>
            </Grid>
        </Grid>
    )
}
